import type {
    DataUpdateStatus,
    DayOffPeriod,
    DayOffStatus,
    DpRequestStatus,
    DpRequestType,
    HealthPlanAction,
    HealthPlanStatus,
    OvertimeCompensationType,
    OvertimeStatus,
    RaroInvestStatus,
    DayOffRequest,
    DpRequest,
    TicketCategory,
    TicketStatus,
} from '../../../shared/dpRequests';

export type StatusTone = 'amber' | 'orange' | 'green' | 'red' | 'blue' | 'gray';

export interface StatusMeta {
    label: string;
    tone: StatusTone;
}

export const TONE_CLASSES: Record<StatusTone, string> = {
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    orange: 'bg-orange-50 text-orange-700 ring-orange-200',
    green: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    red: 'bg-rose-50 text-rose-700 ring-rose-200',
    blue: 'bg-primary-50 text-primary-700 ring-primary-200',
    gray: 'bg-raro-gray-100 text-raro-gray-600 ring-raro-gray-200',
};

export const DATA_UPDATE_STATUS_META: Record<DataUpdateStatus, StatusMeta> = {
    pending_dp: { label: 'Aguardando DP', tone: 'amber' },
    pending_correction: { label: 'Ajuste solicitado', tone: 'orange' },
    approved: { label: 'Aprovada', tone: 'green' },
    rejected: { label: 'Não aprovada', tone: 'red' },
    cancelled: { label: 'Cancelada', tone: 'gray' },
};

export const TICKET_STATUS_META: Record<TicketStatus, StatusMeta> = {
    open: { label: 'Aberto', tone: 'blue' },
    waiting_collaborator: { label: 'Aguardando você', tone: 'amber' },
    resolved: { label: 'Resolvido', tone: 'green' },
    cancelled: { label: 'Cancelado', tone: 'gray' },
};

export const DAY_OFF_STATUS_META: Record<DayOffStatus, StatusMeta> = {
    pending_manager: { label: 'Aguardando aprovações', tone: 'amber' },
    approved: { label: 'Aprovado', tone: 'green' },
    rejected: { label: 'Não aprovado', tone: 'red' },
    cancelled: { label: 'Cancelado', tone: 'gray' },
    expired: { label: 'Expirado', tone: 'gray' },
};

export const OVERTIME_STATUS_META: Record<OvertimeStatus, StatusMeta> = {
    pending_dp: { label: 'Aguardando DP', tone: 'amber' },
    registered: { label: 'Lançada', tone: 'green' },
    rejected: { label: 'Não aprovada', tone: 'red' },
    cancelled: { label: 'Cancelada', tone: 'gray' },
};

export const RARO_INVEST_STATUS_META: Record<RaroInvestStatus, StatusMeta> = {
    pending_dp: { label: 'Aguardando DP', tone: 'amber' },
    approved: { label: 'Aceito, reembolso pendente', tone: 'blue' },
    reimbursed: { label: 'Reembolso realizado', tone: 'green' },
    rejected: { label: 'Não aprovado', tone: 'red' },
    cancelled: { label: 'Cancelado', tone: 'gray' },
};

export const HEALTH_PLAN_STATUS_META: Record<HealthPlanStatus, StatusMeta> = {
    pending_dp: { label: 'Aguardando DP', tone: 'amber' },
    approved: { label: 'Concluído', tone: 'green' },
    rejected: { label: 'Não aprovado', tone: 'red' },
    cancelled: { label: 'Cancelado', tone: 'gray' },
};

export function getStatusMeta(type: DpRequestType, status: DpRequestStatus): StatusMeta {
    if (type === 'data_update') return DATA_UPDATE_STATUS_META[status as DataUpdateStatus];
    if (type === 'ticket') return TICKET_STATUS_META[status as TicketStatus];
    if (type === 'overtime') return OVERTIME_STATUS_META[status as OvertimeStatus];
    if (type === 'raro_invest') return RARO_INVEST_STATUS_META[status as RaroInvestStatus];
    if (type === 'health_plan') return HEALTH_PLAN_STATUS_META[status as HealthPlanStatus];
    return DAY_OFF_STATUS_META[status as DayOffStatus];
}

function describeDayOff(request: DayOffRequest): StatusMeta {
    const managerApproved = Boolean(request.managerDecision);
    const client = request.clientApproval?.status ?? 'not_required';

    if (request.status === 'approved') {
        return { label: client === 'approved' ? 'Aprovado pelo gestor e cliente' : 'Aprovado pelo gestor', tone: 'green' };
    }
    if (request.status === 'rejected') {
        return { label: client === 'rejected' ? 'Recusado pelo cliente' : 'Recusado pelo gestor', tone: 'red' };
    }
    if (request.status === 'pending_manager') {
        if (!managerApproved && client === 'pending') return { label: 'Aguardando gestor e cliente', tone: 'amber' };
        if (managerApproved && client === 'pending') return { label: 'Gestor aprovou, aguardando cliente', tone: 'blue' };
        if (!managerApproved && client === 'approved') return { label: 'Cliente aprovou, aguardando gestor', tone: 'blue' };
        return { label: 'Aguardando gestor', tone: 'amber' };
    }
    return DAY_OFF_STATUS_META[request.status];
}

const DP_DECISION_LABELS: Record<string, string> = {
    'data_update:approved': 'Aprovada pelo DP',
    'data_update:pending_correction': 'Ajuste pedido pelo DP',
    'data_update:rejected': 'Recusada pelo DP',
    'ticket:resolved': 'Resolvido pelo DP',
    'overtime:registered': 'Lançada pelo DP',
    'overtime:rejected': 'Recusada pelo DP',
    'raro_invest:approved': 'Aceito pelo DP, reembolso pendente',
    'raro_invest:reimbursed': 'Reembolsado pelo DP',
    'raro_invest:rejected': 'Recusado pelo DP',
    'health_plan:approved': 'Concluído pelo DP',
    'health_plan:rejected': 'Recusado pelo DP',
};

export function describeRequestStatus(request: DpRequest): StatusMeta {
    if (request.type === 'day_off') return describeDayOff(request);
    const base = getStatusMeta(request.type, request.status);
    const label = DP_DECISION_LABELS[`${request.type}:${request.status}`];
    return label ? { ...base, label } : base;
}

export const COMPENSATION_LABELS: Record<OvertimeCompensationType, string> = {
    compensation: 'Compensação',
    payment: 'Pagamento',
};

export const HEALTH_PLAN_ACTION_LABELS: Record<HealthPlanAction, string> = {
    include: 'Inclusão no plano',
    change: 'Mudança de plano',
    leave: 'Saída do plano',
};

export const TICKET_CATEGORY_OPTIONS: ReadonlyArray<{ value: TicketCategory; label: string; description: string }> = [
    {
        value: 'reserved',
        label: 'Reservado',
        description: 'Só você e o DP veem este chamado. Use para desligamento ou assuntos sensíveis.',
    },
    {
        value: 'other',
        label: 'Outro assunto',
        description: 'Folha, benefícios, holerite, declarações ou qualquer dúvida.',
    },
];

export const DAY_OFF_PERIOD_OPTIONS: ReadonlyArray<{ value: DayOffPeriod; label: string }> = [
    { value: 'full', label: 'Dia inteiro' },
    { value: 'morning', label: 'Manhã' },
    { value: 'afternoon', label: 'Tarde' },
];

export const DP_QUEUE_TABS = ['pending', 'waiting', 'closed'] as const;
export type DpQueueTab = (typeof DP_QUEUE_TABS)[number];

export const DP_QUEUE_TAB_LABELS: Record<DpQueueTab, string> = {
    pending: 'Pendentes',
    waiting: 'Aguardando colaborador',
    closed: 'Concluídos',
};

export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const ATTACHMENT_ACCEPT = 'application/pdf,image/jpeg,image/png';
export const ATTACHMENT_ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

export const REQUEST_TYPE_LABELS: Record<DpRequestType, string> = {
    data_update: 'Atualização de dados',
    ticket: 'Outros assuntos',
    day_off: 'Day-off',
    overtime: 'Hora extra',
    raro_invest: 'Raro Invest',
    health_plan: 'Plano de saúde',
};

export const DP_ROLE_LIST = ['admin', 'vacation_dp'] as const;
